import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Search,
  Clock,
  Eye,
  User,
  ArrowRight,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  Sparkles,
  FileText,
  Pencil,
  Trash2,
  Send
} from 'lucide-react';
import { isNativePlatform, hasFullAccess } from '../lib/platform';
import { Button } from '../components/ui/button';

const CATEGORIES = [
  { id: 'all', label: 'Tous les articles' },
  { id: 'investissement', label: "Stratégies" },
  { id: 'fiscalite', label: 'Fiscalité' },
  { id: 'temoignages', label: 'Témoignages' },
  { id: 'actualites', label: 'Actualités' },
  { id: 'guides', label: 'Guides' },
];

const CATEGORY_COLORS = {
  investissement: 'bg-[#0D47A1]/10 text-[#0D47A1]',
  fiscalite: 'bg-emerald-50 text-emerald-700',
  temoignages: 'bg-amber-50 text-amber-700',
  actualites: 'bg-rose-50 text-rose-700',
  guides: 'bg-violet-50 text-violet-700',
};

const BlogPage = () => {
  const { user } = useAuth();
  const isClubMember = hasFullAccess(user?.subscription_tier) || user?.subscription_plan === 'club' || user?.role === 'admin';
  const [articles, setArticles] = useState([]);
  const [myArticles, setMyArticles] = useState([]);
  const [showMyArticles, setShowMyArticles] = useState(false);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => { fetchArticles(); }, [category, page]);
  useEffect(() => { if (user && showMyArticles) fetchMyArticles(); }, [user, showMyArticles]);

  const fetchMyArticles = async () => {
    try {
      const res = await axios.get(`${API}/blog/my-articles`, { withCredentials: true });
      setMyArticles(res.data?.articles || res.data || []);
    } catch (err) { console.error(err); }
  };

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 12 });
      if (category !== 'all') params.append('category', category);
      if (search) params.append('search', search);
      const res = await axios.get(`${API}/blog/articles?${params}`);
      setArticles(res.data.articles || []);
      setTotalPages(res.data.pages || 1);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleSearch = (e) => { e.preventDefault(); setPage(1); fetchArticles(); };

  const formatDate = (d) => {
    if (!d) return '';
    const date = new Date(d);
    const now = new Date();
    const diff = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    if (diff === 0) return "Aujourd'hui";
    if (diff === 1) return 'Hier';
    if (diff < 7) return `Il y a ${diff} jours`;
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const getCategoryLabel = (id) => CATEGORIES.find(c => c.id === id)?.label || id;
  const featured = articles[0];
  const rest = articles.slice(1);

  return (
    <div className="min-h-screen bg-[#fafafa]">
      {/* Minimal Header */}
      <nav className="bg-white border-b border-slate-100 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2 sm:gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_4ed8f34b-1bc9-42f5-8577-4843189c4948/artifacts/jd7g0w5b_LOGO%2010MILLIONSINVESTOR%20blanc.png"
              alt="10M Investor"
              className="h-6 sm:h-7"
            />
            <div className="h-5 w-px bg-slate-200" />
            <span className="text-sm font-semibold text-slate-800 tracking-tight">Blog</span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-4">
            <form onSubmit={handleSearch} className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <input
                placeholder="Rechercher..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 pr-4 py-1.5 text-sm bg-slate-50 border border-slate-200 rounded-full w-32 sm:w-52 focus:outline-none focus:ring-2 focus:ring-[#0D47A1]/20 focus:border-[#0D47A1]/30 transition-all"
                data-testid="blog-search"
              />
            </form>
            {isClubMember && (
              <>
                <button
                  onClick={() => setShowMyArticles(!showMyArticles)}
                  className={`text-sm font-medium hidden sm:flex items-center gap-1 ${showMyArticles ? 'text-[#00B050]' : 'text-slate-500 hover:text-slate-700'}`}
                  data-testid="my-articles-btn"
                >
                  <FileText className="w-3.5 h-3.5" />
                  Mes articles
                </button>
                <Link to="/blog/write" className="text-sm font-medium text-[#0D47A1] hover:text-[#0D47A1]/80 hidden sm:flex items-center gap-1">
                  <Sparkles className="w-3.5 h-3.5" />
                  Écrire
                </Link>
              </>
            )}
            {user ? (
              <Link to="/dashboard" className="text-xs text-slate-500 hover:text-slate-700 hidden sm:block">Dashboard</Link>
            ) : (
              <Link to="/login" className="text-xs font-medium text-[#0D47A1] hover:text-[#0D47A1]/80 hidden sm:block">Se connecter</Link>
            )}
          </div>
        </div>
      </nav>

      {/* Hero section */}
      <header className="bg-white border-b border-slate-100 overflow-hidden">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            {/* Left: Text */}
            <div>
              <div className="flex items-center gap-2 mb-3 sm:mb-4">
                <div className="w-1.5 h-1.5 rounded-full bg-[#00B050] animate-pulse" />
                <span className="text-xs font-semibold text-[#00B050] uppercase tracking-wider">Blog Communautaire</span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 leading-[1.1] mb-3 sm:mb-4 tracking-tight">
                Investir<br />plus intelligemment.
              </h1>
              <p className="text-base sm:text-lg text-slate-500 leading-relaxed max-w-lg">
                Stratégies, analyses de marché et retours d'expérience partagés par notre communauté d'investisseurs immobiliers.
              </p>
            </div>

            {/* Right: Phone Mockup */}
            <div className="hidden lg:flex justify-end items-center">
              <div className="relative">
                {/* Phone */}
                <div className="w-[220px] h-[460px] bg-slate-900 rounded-[2.5rem] p-2.5 shadow-2xl transform rotate-[-4deg] hover:rotate-0 transition-transform duration-500">
                  <div className="w-full h-full bg-white rounded-[2rem] overflow-hidden relative">
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-5 bg-slate-900 rounded-b-xl z-20" />
                    <div className="w-full h-full relative">
                      <div className="absolute inset-0 bg-cover bg-center" style={{backgroundImage: "url('https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=400&q=80')"}}>
                        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
                      </div>
                      <div className="relative z-10 w-full h-full pt-8 flex flex-col">
                        <div className="px-3 py-2">
                          <img src="/logo-horizontal.png" alt="10M" className="h-5 w-auto brightness-0 invert" />
                        </div>
                        <div className="px-3 pt-3">
                          <p className="text-white/70 text-[9px] uppercase tracking-wider">Votre prochain investissement</p>
                          <h3 className="text-white text-sm font-bold mt-0.5">Appartement T3 Lyon</h3>
                          <p className="text-white/80 text-[10px] mt-0.5">85m² - 2 chambres</p>
                        </div>
                        <div className="px-3 mt-3 space-y-1.5">
                          <div className="bg-white/15 backdrop-blur-md rounded-lg p-2.5 border border-white/20">
                            <div className="flex justify-between items-center">
                              <div>
                                <div className="text-white/70 text-[9px]">Rendement brut</div>
                                <div className="text-white text-base font-bold">6.2%</div>
                              </div>
                              <div className="w-7 h-7 rounded-full bg-[#00B050] flex items-center justify-center">
                                <TrendingUp className="h-3.5 w-3.5 text-white" />
                              </div>
                            </div>
                          </div>
                          <div className="bg-[#00B050]/90 backdrop-blur-md rounded-lg p-2.5">
                            <div className="flex justify-between items-center">
                              <div>
                                <div className="text-white/90 text-[9px]">Cash-flow mensuel</div>
                                <div className="text-white text-base font-bold">+245 €</div>
                              </div>
                              <div className="text-white/80 text-[9px] text-right font-semibold">Rentable</div>
                            </div>
                          </div>
                        </div>
                        <div className="mt-auto px-3 pb-5">
                          <div className="grid grid-cols-3 gap-1.5">
                            {['Blog', 'MDB', 'Club'].map(label => (
                              <div key={label} className="bg-white/15 backdrop-blur rounded-lg py-1.5 text-center">
                                <div className="text-white text-[9px] font-medium">{label}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Floating badge */}
                <div className="absolute -bottom-8 -left-16 bg-white rounded-2xl p-4 shadow-xl border border-slate-100">
                  <img
                    src="https://customer-assets.emergentagent.com/job_4ed8f34b-1bc9-42f5-8577-4843189c4948/artifacts/hmrw1on2_LOGO%2010MILLIONSINVESTOR%20blanc.png"
                    alt="10M Investor"
                    className="h-14 w-auto"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Categories - horizontal scroll */}
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pb-4">
          <div className="flex gap-1.5 overflow-x-auto scrollbar-hide -mx-1 px-1 pb-1">
            {CATEGORIES.map(cat => (
              <button
                key={cat.id}
                onClick={() => { setCategory(cat.id); setPage(1); }}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                  category === cat.id
                    ? 'bg-slate-900 text-white shadow-sm'
                    : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100'
                }`}
                data-testid={`blog-cat-${cat.id}`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* My Articles Panel */}
      {showMyArticles && user && (
        <div className="bg-white border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#0D47A1]" />
                Mes articles ({myArticles.length})
              </h2>
              <Link to="/blog/write">
                <Button size="sm" className="bg-[#0D47A1] text-white">
                  <Sparkles className="w-4 h-4 mr-1" /> Nouvel article
                </Button>
              </Link>
            </div>
            {myArticles.length === 0 ? (
              <p className="text-slate-500 text-sm py-4">Vous n'avez pas encore d'articles.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {myArticles.map(article => (
                  <div key={article.article_id} className="bg-slate-50 rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow" data-testid={`my-article-${article.article_id}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={
                        article.status === 'published' ? 'bg-green-100 text-green-700' :
                        article.status === 'draft' ? 'bg-slate-100 text-slate-600' :
                        article.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                        'bg-red-100 text-red-700'
                      }>
                        {article.status === 'published' ? 'Publié' :
                         article.status === 'draft' ? 'Brouillon' :
                         article.status === 'pending' ? 'En attente' : 'Refusé'}
                      </Badge>
                      {article.category && (
                        <Badge className="bg-blue-50 text-blue-600 text-xs">{getCategoryLabel(article.category)}</Badge>
                      )}
                    </div>
                    <h3 className="font-semibold text-slate-900 text-sm mb-1 line-clamp-2">{article.title || 'Sans titre'}</h3>
                    <p className="text-xs text-slate-500 mb-3">{formatDate(article.created_at)}</p>
                    <div className="flex gap-2">
                      {article.status === 'draft' && (
                        <Link to={`/blog/write?edit=${article.article_id}`} className="flex-1">
                          <Button size="sm" variant="outline" className="w-full text-xs">
                            <Pencil className="w-3 h-3 mr-1" /> Modifier
                          </Button>
                        </Link>
                      )}
                      {article.status === 'published' && (
                        <Link to={`/blog/${article.slug}`} className="flex-1">
                          <Button size="sm" variant="outline" className="w-full text-xs">
                            <Eye className="w-3 h-3 mr-1" /> Voir
                          </Button>
                        </Link>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-10">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[1,2,3,4].map(i => (
              <div key={i} className="animate-pulse rounded-2xl overflow-hidden">
                <div className="bg-slate-200 h-56" />
                <div className="p-6 space-y-3">
                  <div className="bg-slate-200 h-3 w-20 rounded-full" />
                  <div className="bg-slate-200 h-5 w-3/4 rounded" />
                  <div className="bg-slate-200 h-3 w-full rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : articles.length === 0 ? (
          <div className="text-center py-24">
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-slate-100 flex items-center justify-center">
              <BookOpen className="w-10 h-10 text-slate-300" />
            </div>
            <h3 className="text-2xl font-bold text-slate-800 mb-2">Bientôt ici</h3>
            <p className="text-slate-500 max-w-md mx-auto">Les premiers articles de notre communauté arrivent très prochainement.</p>
          </div>
        ) : (
          <>
            {/* Featured Article */}
            {featured && (
              <Link to={`/blog/${featured.slug}`} className="group block mb-10" data-testid="blog-featured">
                <article className="relative bg-white rounded-3xl overflow-hidden border border-slate-100 hover:shadow-2xl hover:shadow-slate-200/50 transition-all duration-500">
                  <div className="grid md:grid-cols-2">
                    <div className="relative h-64 md:h-auto md:min-h-[360px] overflow-hidden">
                      {featured.cover_image ? (
                        <img
                          src={featured.cover_image}
                          alt={featured.title}
                          className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                        />
                      ) : (
                        <div className="absolute inset-0 bg-gradient-to-br from-[#0D47A1] to-[#00B050] flex items-center justify-center">
                          <BookOpen className="w-16 h-16 text-white/30" />
                        </div>
                      )}
                      <div className="absolute top-4 left-4">
                        <Badge className="bg-white/90 backdrop-blur-sm text-slate-800 shadow-sm font-semibold">
                          <TrendingUp className="w-3 h-3 mr-1" />
                          À la une
                        </Badge>
                      </div>
                    </div>
                    <div className="p-5 sm:p-8 md:p-10 flex flex-col justify-center">
                      <Badge className={`w-fit mb-3 sm:mb-4 ${CATEGORY_COLORS[featured.category] || 'bg-slate-100 text-slate-600'}`}>
                        {getCategoryLabel(featured.category)}
                      </Badge>
                      <h2 className="text-xl sm:text-2xl md:text-3xl font-black text-slate-900 leading-tight mb-3 sm:mb-4 group-hover:text-[#0D47A1] transition-colors duration-300">
                        {featured.title}
                      </h2>
                      <p className="text-slate-500 leading-relaxed mb-4 sm:mb-6 line-clamp-3 text-sm sm:text-base">{featured.excerpt}</p>
                      <div className="flex items-center gap-3 sm:gap-4 text-sm text-slate-400 flex-wrap">
                        <span className="flex items-center gap-1.5">
                          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#0D47A1] to-[#00B050] flex items-center justify-center text-white text-xs font-bold">
                            {(featured.author_name || '?')[0]}
                          </div>
                          <span className="font-medium text-slate-600">{featured.author_name}</span>
                        </span>
                        <span className="text-slate-300">|</span>
                        <span>{formatDate(featured.published_at)}</span>
                        <span className="text-slate-300">|</span>
                        <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{featured.views}</span>
                      </div>
                    </div>
                  </div>
                </article>
              </Link>
            )}

            {/* Grid */}
            {rest.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {rest.map((article, idx) => (
                  <Link
                    key={article.article_id || idx}
                    to={`/blog/${article.slug}`}
                    className="group"
                    data-testid={`blog-article-${idx}`}
                  >
                    <article className="bg-white rounded-2xl overflow-hidden border border-slate-100 hover:shadow-xl hover:shadow-slate-200/50 transition-all duration-500 hover:-translate-y-1 h-full flex flex-col">
                      <div className="relative h-52 overflow-hidden">
                        {article.cover_image ? (
                          <img
                            src={article.cover_image}
                            alt={article.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-slate-100 to-slate-50 flex items-center justify-center">
                            <BookOpen className="w-10 h-10 text-slate-200" />
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      </div>
                      <div className="p-5 flex-1 flex flex-col">
                        <Badge className={`w-fit mb-3 text-xs ${CATEGORY_COLORS[article.category] || 'bg-slate-100 text-slate-600'}`}>
                          {getCategoryLabel(article.category)}
                        </Badge>
                        <h3 className="font-bold text-slate-900 mb-2 leading-snug line-clamp-2 group-hover:text-[#0D47A1] transition-colors duration-300 flex-1">
                          {article.title}
                        </h3>
                        <p className="text-sm text-slate-500 line-clamp-2 mb-4 leading-relaxed">{article.excerpt}</p>
                        <div className="flex items-center justify-between text-xs text-slate-400 mt-auto pt-4 border-t border-slate-50">
                          <div className="flex items-center gap-2">
                            <div className="w-5 h-5 rounded-full bg-gradient-to-br from-[#0D47A1] to-[#00B050] flex items-center justify-center text-white text-[9px] font-bold">
                              {(article.author_name || '?')[0]}
                            </div>
                            <span className="font-medium text-slate-600">{article.author_name}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span>{formatDate(article.published_at)}</span>
                            <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{article.views}</span>
                          </div>
                        </div>
                      </div>
                    </article>
                  </Link>
                ))}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-3 mt-12">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center disabled:opacity-30 hover:bg-slate-50 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-slate-500 font-medium">{page} / {totalPages}</span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center disabled:opacity-30 hover:bg-slate-50 transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        )}
      </main>

      {/* Newsletter / CTA */}
      <section className="border-t border-slate-100 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="max-w-xl mx-auto text-center">
            <span className="text-xs font-semibold text-[#00B050] uppercase tracking-wider">Contribuer</span>
            <h3 className="text-2xl font-black text-slate-900 mt-2 mb-3">Partagez votre expertise</h3>
            <p className="text-slate-500 mb-6 leading-relaxed">
              Les membres du Club peuvent rédiger des articles avec l'aide de notre assistant IA et les partager avec toute la communauté.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              {isClubMember ? (
                <Link
                  to="/blog/write"
                  className="inline-flex items-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-full font-semibold hover:bg-slate-800 transition-colors text-sm"
                >
                  <Sparkles className="w-4 h-4" />
                  Rédiger un article
                </Link>
              ) : (
                isNativePlatform() ? (
                  <span className="inline-flex items-center gap-2 text-slate-500 text-sm font-medium">
                    Bientôt disponible
                  </span>
                ) : (
                <Link
                  to="/pricing"
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-[#00B050] to-[#0D47A1] text-white px-6 py-3 rounded-full font-semibold hover:opacity-90 transition-opacity text-sm"
                >
                  Rejoindre le Club pour écrire
                  <ArrowRight className="w-4 h-4" />
                </Link>
                )
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-100 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between text-xs text-slate-400">
          <span>10 Millions Investor</span>
          <Link to={user ? "/dashboard" : "/"} className="hover:text-slate-600">{user ? 'Retour au dashboard' : 'Retour au site'}</Link>
        </div>
      </footer>
    </div>
  );
};

export default BlogPage;
